import React, { useLayoutEffect, useRef } from "react";
import ArtistProfile from "./ArtistProfile";

type ProfileSection = "account" | "password" | "artist";

const hiddenSectionTitlesByActiveSection: Record<ProfileSection, string[]> = {
  account: [
    "Change Password",
    "Artist Information",
    "Distribution Information",
    "Artist Account Links",
  ],
  password: [
    "Account Information",
    "Artist Information",
    "Distribution Information",
    "Artist Account Links",
  ],
  artist: ["Account Information", "Change Password"],
};

const ProfileSectionedArtistProfile: React.FC<{ activeSection: ProfileSection }> = ({ activeSection }) => {
  const hiddenContainersRef = useRef<HTMLElement[]>([]);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const reapplyTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const applyVisibility = () => {
      // Clear any previous hides (keeps navigation safe).
      hiddenContainersRef.current.forEach((el) => {
        el.style.display = "";
      });
      hiddenContainersRef.current = [];

      const titlesToHide = [
        "Profile Info",
        ...(hiddenSectionTitlesByActiveSection[activeSection] || []),
      ];
      const rootEl = rootRef.current;
      if (!rootEl) return;

      const spans = Array.from(rootEl.querySelectorAll("span"));
      const findSpanByText = (title: string) => {
        // Prefer the hidden marker span (we inject these per section) so we hide the correct wrapper,
        // not the visible sticky header text span.
        const matching = spans.filter((el) => (el.textContent || "").trim() === title) as HTMLSpanElement[];
        return matching.find((el) => el.classList.contains("hidden")) || matching[0];
      };

      const hideSection = (title: string) => {
        const headerSpan = findSpanByText(title);
        if (!headerSpan) return;

        if (title === "Profile Info") {
          // Hide only the top heading text, not its wrapper container.
          headerSpan.style.display = "none";
          hiddenContainersRef.current.push(headerSpan);
          return;
        }

        // For section marker spans (hidden), the closest div is the section wrapper.
        const container = headerSpan.closest("div") as HTMLElement | null;
        if (!container) return;

        container.style.display = "none";
        hiddenContainersRef.current.push(container);
      };

      titlesToHide.forEach(hideSection);
    };

    // Apply now (works if headers exist on first render).
    applyVisibility();

    // Re-apply after `ArtistProfile` finishes loading (headers appear later).
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const rootEl = rootRef.current;
    if (!rootEl) return;

    observerRef.current = new MutationObserver(() => {
      // Throttle re-application to avoid doing too much work during DOM updates.
      if (reapplyTimerRef.current) return;
      reapplyTimerRef.current = window.setTimeout(() => {
        reapplyTimerRef.current = null;
        applyVisibility();
      }, 0);
    });

    observerRef.current.observe(rootEl, { subtree: true, childList: true, attributes: true });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (reapplyTimerRef.current) {
        window.clearTimeout(reapplyTimerRef.current);
        reapplyTimerRef.current = null;
      }
      hiddenContainersRef.current.forEach((el) => {
        el.style.display = "";
      });
      hiddenContainersRef.current = [];
    };
  }, [activeSection]);

  return (
    <div ref={rootRef}>
      <ArtistProfile />
    </div>
  );
};

export default ProfileSectionedArtistProfile;

